import { InternalLink } from "components/atoms/InternalLink";
import { paths } from "enum/paths.enum";
import { useRouter } from "next/router";
import { FinancialCompany } from "enum/financial-company.enum";
import { useServicesNewTag } from "components/pages/Services/useServicesNewTag";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { isUserAdmin } from "utils/isUserAdmin";
import {
  isUserAdminForSalesAccounts,
  isUserSalesPeopleForSalesAccounts,
} from "utils/isUserAdminForSalesAccounts";
import { canSalesAccountSeeExceptions } from "components/pages/Exceptions/exceptions.constants";
import { canSeeExceptionsV2 } from "components/pages/ExceptionsV2/exceptionsV2.constants";
import { PermissionCheck } from "utils/PermissionCheck/PermissionCheck";
import { PICKUP_ELIGIBLE_API_PATHS } from "constants/pickup-eligible-carriers.const";
import { createElement, useEffect, useState } from "react";
import { IconType } from "react-icons";
import { Tooltip } from "components/atoms/Tooltip";
import {
  FaBox,
  FaTag,
  FaUpload,
  FaTruck,
  FaCog,
  FaQuestion,
  FaChartBar,
  FaExclamationTriangle,
  FaFolderOpen,
  FaShippingFast,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
} from "react-icons/fa";
import cx from "classnames";
import styled from "styled-components";
import { useTheme } from "styled-components";
import Image from "next/image";
// Sidebar logo assets. The collapsed marks are named for the theme they appear
// in, but the uncollapsed wordmarks are named for their ink colour: the
// "dark-mode" wordmark is dark ink, so it belongs to the light theme.
import WordmarkLightTheme from "../../../public/images/dark-mode-icon-uncollapsed.svg";
import WordmarkDarkTheme from "../../../public/images/light-mode-icon-uncollapsed.svg";
import MarkLightTheme from "../../../public/images/light-mode-icon-collapsed.svg";
import MarkDarkTheme from "../../../public/images/dark-mode-icon-collapsed.svg";

const HIDE_EXCEPTIONS_STORAGE_KEY = "sidebar-hide-exceptions";

const StyledSidebar = styled.div<{ $collapsed: boolean }>`
  width: ${(props) => (props.$collapsed ? "60px" : "198px")};
  flex-shrink: 0;
  background-color: ${(props) => props.theme.navigationBackgroundColor};
  border-right: 1px solid ${(props) => props.theme.borderColor};
  transition: width 0.2s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* Phone-sized viewports have no sidebar (navigation lives in the header's
     menu drawer). LayoutDashboard stops rendering it after hydration; this
     covers the SSR paint before that. */
  @media (max-width: 767px) {
    display: none;
  }
`;

const SidebarLink = ({
  icon,
  label,
  isActive,
  collapsed,
  count,
  isNew,
}: {
  icon: IconType;
  label: string;
  isActive: boolean;
  collapsed: boolean;
  count?: number;
  /** Shows a "New" tag (a dot on the icon when collapsed). */
  isNew?: boolean;
}) => {
  const inner = (
    <div
      className={cx(
        "flex items-center h-10 text-sm cursor-pointer rounded-md transition-all font-medium leading-none select-none",
        {
          "bg-gray4 text-gray12": isActive,
          "text-gray11 hover:bg-gray3 hover:text-gray12": !isActive,
        }
      )}
    >
      <div className="relative w-11 h-10 flex items-center justify-center flex-shrink-0 text-sm">
        {createElement(icon)}
        {collapsed && isNew && (
          <span
            className="absolute w-2 h-2 rounded-full top-2 right-2 bg-red9"
            aria-hidden="true"
          />
        )}
      </div>
      {!collapsed && <span className="truncate leading-normal">{label}</span>}
      {!collapsed && isNew && (
        <span className="flex-shrink-0 ml-2 px-[5px] py-[3px] text-[10px] font-bold leading-none text-white rounded-full bg-red9">
          New
        </span>
      )}
      {!collapsed && typeof count === "number" && (
        <span
          className="flex items-center justify-center flex-shrink-0 ml-2 text-[11px] font-semibold leading-none text-white rounded-full bg-red10"
          style={{ minWidth: 18, height: 18, paddingLeft: 5, paddingRight: 5 }}
        >
          {count}
        </span>
      )}
    </div>
  );

  if (!collapsed) return inner;

  return (
    <Tooltip content={label} side="right" asChild>
      {inner}
    </Tooltip>
  );
};

export const AppSidebar = ({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) => {
  const { pathname } = useRouter();
  const showServicesNew = useServicesNewTag();
  const sidebarTheme = useTheme();
  const { theme: themeName } = useSelector(
    (state: RootState) => state.tableSlice
  );
  const wordmarkLogo =
    themeName === "dark" ? WordmarkDarkTheme : WordmarkLightTheme;
  const markLogo = themeName === "dark" ? MarkDarkTheme : MarkLightTheme;

  const { loggedInUser, financialCompany } = useSelector(
    (state: RootState) => state.authSlice
  );
  const { settings } = useSelector((state: RootState) => state.settingsSlice);
  const permissions = new PermissionCheck({ user: loggedInUser });

  const canSeeExceptions =
    isUserAdminForSalesAccounts(loggedInUser) ||
    isUserSalesPeopleForSalesAccounts(loggedInUser) ||
    canSalesAccountSeeExceptions(settings);
  const canSeeV2 = canSeeExceptionsV2(loggedInUser);

  // ITD staff admins and salespeople, plus accounts enabled via the FFAST
  // "Show Exceptions page" toggle. Shown by default; Cmd/Ctrl+Shift+8 hides
  // it.
  // Session-persisted so the choice survives route changes. Initialised in an
  // effect so server and client render the same HTML.
  const [showExceptions, setShowExceptions] = useState(false);
  useEffect(() => {
    setShowExceptions(
      window.sessionStorage.getItem(HIDE_EXCEPTIONS_STORAGE_KEY) !== "true"
    );
  }, []);

  useEffect(() => {
    if (!canSeeExceptions) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey || e.code !== "Digit8")
        return;

      e.preventDefault();
      setShowExceptions((current) => {
        window.sessionStorage.setItem(
          HIDE_EXCEPTIONS_STORAGE_KEY,
          String(current)
        );
        return !current;
      });
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [canSeeExceptions]);

  const { shippingWhitelists } = useSelector(
    (state: RootState) => state.shippingWhitelistsSlice
  );

  const containsUpsOrFedex = shippingWhitelists.filter((el) =>
    PICKUP_ELIGIBLE_API_PATHS.includes(el.supplier.apiPath)
  );

  const quotesHref = isUserAdmin(loggedInUser)
    ? paths.SHIPMENTS_INDEX
    : paths.QUOTES_INDEX;
  const quotesActive = isUserAdmin(loggedInUser)
    ? pathname === paths.SHIPMENTS_INDEX
    : pathname === paths.QUOTES_INDEX;

  return (
    <StyledSidebar $collapsed={collapsed}>
      {collapsed ? (
        <div className="flex flex-col items-center flex-shrink-0 pt-2 gap-1">
          <InternalLink href={paths.DASHBOARD_INDEX}>
            <div className="w-9 h-9 flex items-center justify-center cursor-pointer">
              <Image src={markLogo} alt="Connexx" className="w-6 h-auto" />
            </div>
          </InternalLink>
          <button
            onClick={onToggle}
            title="Expand sidebar"
            className="w-9 h-9 flex items-center justify-center rounded-md text-gray9 hover:text-gray12 hover:bg-gray3 transition-all"
          >
            <FaAngleDoubleRight size={14} />
          </button>
        </div>
      ) : (
        <div
          className="flex items-center flex-shrink-0"
          style={{ height: sidebarTheme.navigationHeight }}
        >
          <InternalLink href={paths.DASHBOARD_INDEX} className="flex-1 min-w-0">
            <div className="flex items-center pl-[22px] pr-2 cursor-pointer">
              <Image
                src={wordmarkLogo}
                alt="Connexx"
                className="w-full h-auto"
              />
            </div>
          </InternalLink>
          <button
            onClick={onToggle}
            title="Collapse sidebar"
            className="w-9 h-9 flex items-center justify-center rounded-md text-gray9 hover:text-gray12 hover:bg-gray3 transition-all flex-shrink-0 mr-2"
          >
            <FaAngleDoubleLeft size={14} />
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto overflow-x-hidden py-2 px-2 flex flex-col gap-1">
        {permissions.shipments.canView() && (
          <InternalLink href={paths.SHIPMENTS_INDEX + "?status=READY"}>
            <SidebarLink
              icon={FaBox}
              label="Shipments"
              isActive={pathname === paths.SHIPMENTS_INDEX}
              collapsed={collapsed}
            />
          </InternalLink>
        )}

        <InternalLink href={paths.INTELLIGENCE_INDEX}>
          <SidebarLink
            icon={FaChartBar}
            label="Intelligence"
            isActive={pathname === paths.INTELLIGENCE_INDEX}
            collapsed={collapsed}
          />
        </InternalLink>

        {/* ITD UK services, so never on Global American accounts */}
        {financialCompany !== FinancialCompany.GlobalAmerican && (
          <InternalLink href={paths.SERVICES_INDEX}>
            <SidebarLink
              icon={FaShippingFast}
              label="Services"
              isActive={pathname === paths.SERVICES_INDEX}
              collapsed={collapsed}
              isNew={showServicesNew}
            />
          </InternalLink>
        )}

        <InternalLink href={quotesHref}>
          <SidebarLink
            icon={FaTag}
            label="Quotes"
            isActive={quotesActive}
            collapsed={collapsed}
          />
        </InternalLink>

        {permissions.bulkShipments.canView() && (
          <InternalLink href={paths.BULK_SHIPMENTS_INDEX}>
            <SidebarLink
              icon={FaUpload}
              label="Uploads"
              isActive={pathname === paths.BULK_SHIPMENTS_INDEX}
              collapsed={collapsed}
            />
          </InternalLink>
        )}

        {permissions.pickup.canView() && containsUpsOrFedex.length > 0 && (
          <InternalLink href={paths.PICKUPS_INDEX}>
            <SidebarLink
              icon={FaTruck}
              label="Pickups"
              isActive={pathname === paths.PICKUPS_INDEX}
              collapsed={collapsed}
            />
          </InternalLink>
        )}

        {canSeeExceptions && showExceptions && (
          <InternalLink href={paths.EXCEPTIONS_INDEX}>
            <SidebarLink
              icon={FaExclamationTriangle}
              label="Exceptions"
              isActive={pathname === paths.EXCEPTIONS_INDEX}
              collapsed={collapsed}
            />
          </InternalLink>
        )}

        {/* ITD staff only; hidden by the same shortcut as Exceptions */}
        {canSeeV2 && showExceptions && (
          <InternalLink href={paths.EXCEPTIONS_V2_INDEX}>
            <SidebarLink
              icon={FaExclamationTriangle}
              label="Exceptions v2"
              isActive={pathname === paths.EXCEPTIONS_V2_INDEX}
              collapsed={collapsed}
            />
          </InternalLink>
        )}

        <InternalLink href={paths.CASES_INDEX}>
          <SidebarLink
            icon={FaFolderOpen}
            label="Support Cases"
            isActive={pathname.startsWith(paths.CASES_INDEX)}
            collapsed={collapsed}
          />
        </InternalLink>

      </div>

      <div className="py-2 px-2 flex flex-col gap-1">
        {permissions.settings.canEdit() && (
          <InternalLink href={paths.COMPANY_SETTINGS_GENERAL}>
            <SidebarLink
              icon={FaCog}
              label="Settings"
              isActive={
                pathname.startsWith("/settings") &&
                pathname !== paths.HELP_SETTINGS
              }
              collapsed={collapsed}
            />
          </InternalLink>
        )}
        <InternalLink href={paths.HELP_SETTINGS}>
          <SidebarLink
            icon={FaQuestion}
            label="Help"
            isActive={pathname === paths.HELP_SETTINGS}
            collapsed={collapsed}
          />
        </InternalLink>
      </div>
    </StyledSidebar>
  );
};
